import PropTypes from 'prop-types';
import RichText from '@activebrands/core-web/libs/storyblok/RichText';
import { useStyletron } from '@activebrands/core-web/libs/styletron';
import responsiveTextStyles from '@activebrands/core-web/utils/responsive-text-styles';
import getPositionStyles from '@activebrands/core-web/utils/style-functions/get-position-styles';
import Heading from 'components/text/Heading';
import Paragraph from 'components/text/Paragraph';

// Decide fontKeys based on the blocks context
const fontKeys = {
    oneColumn: {
        heading: ['Primary/48_-09', null, null, null, null, 'Primary/56_-105'],
        paragraph: ['Secondary/16_130_-02'],
        preamble: ['Secondary/18_120_-0225', null, null, 'Misc/24_120_-03'],
        subHeading: ['Secondary/28_100_-0525'],
        testimonial: ['Primary/32_-04', null, null, 'Primary/56_-105'],
        quote: {
            text: ['Primary/32_-04', null, null, 'Primary/56_-105'],
            author: ['Secondary/14_130_-0175'],
        },
    },
    twoColumns: {
        heading: ['Primary/48_-09', null, null, null, null, 'Primary/56_-105'],
        paragraph: ['Secondary/16_130_-02'],
        preamble: ['Secondary/18_120_-0225', null, null, 'Misc/24_120_-03'],
        subHeading: ['Secondary/28_100_-0525'],
        testimonial: ['Primary/32_-04', null, null, 'Primary/56_-105'],
        quote: {
            text: ['Primary/32_-04', null, null, 'Primary/56_-105'],
            author: ['Secondary/14_130_-0175'],
        },
    },
    threeColumns: {
        heading: ['Primary/48_-09', null, null, null, null, 'Primary/56_-105'],
        paragraph: ['Secondary/16_130_-02'],
        preamble: ['Secondary/18_120_-0225', null, null, 'Misc/24_120_-03'],
        subHeading: ['Secondary/28_100_-0525'],
        testimonial: ['Primary/32_-04', null, null, 'Primary/56_-105'],
        quote: {
            text: ['Primary/32_-04', null, null, 'Primary/56_-105'],
            author: ['Secondary/14_130_-0175'],
        },
    },
};

const Text = ({ contentHorizontalAlignment = 'left', id = '', items = [], layout = 'oneColumn' }) => {
    const [css] = useStyletron();

    if (!items?.length) {
        return null;
    }

    const textComponentFontKeys = fontKeys[layout];

    const textComponentStyling = {
        oneColumn: {
            heading: { marginBottom: ['32px', null, null, '48px'] },
            paragraph: { marginBottom: ['16px', null, null, '24px'] },
            preamble: { marginBottom: ['32px', null, null, '24px'] },
            subHeading: { marginBottom: ['16px', null, null, '24px'] },
            testimonial: { textAlign: 'center', padding: '40px 0' },
            quote: { textAlign: 'center', padding: '40px 0' },
        },
        twoColumns: {
            heading: { marginBottom: ['32px', null, null, '48px'] },
            paragraph: { marginBottom: ['16px', null, null, '24px'] },
            preamble: { marginBottom: ['32px', null, null, '24px'] },
            subHeading: { marginBottom: ['16px', null, null, '24px'] },
            testimonial: { textAlign: 'center', padding: '40px 0' },
            quote: { textAlign: 'center', padding: '40px 0' },
        },
        threeColumns: {
            heading: { marginBottom: ['32px', null, null, '48px'] },
            paragraph: { marginBottom: ['16px', null, null, '24px'] },
            preamble: { marginBottom: ['32px', null, null, '24px'] },
            subHeading: { marginBottom: ['16px', null, null, '24px'] },
            testimonial: { textAlign: 'center', padding: '40px 0' },
            quote: { textAlign: 'center', padding: '40px 0' },
        },
    };

    const contentWrapperStyles = {
        'oneColumn': { width: ['100%', null, null, null, null, '50%'] },
        'twoColumns': { width: '100%', padding: ['var(--margin-content)', null, null, '0 var(--margin-content)'] },
        'threeColumns': { width: '100%', padding: ['var(--margin-content)', null, null, '0 var(--margin-content)'] },
    };

    const positionStyles = getPositionStyles({
        contentHorizontalAlignment,
        contentVerticalAlignment: 'top',
    });

    return (
        <div className={css({ display: 'flex', ...positionStyles })} id={id}>
            <div className={css({ ...contentWrapperStyles[layout] })}>
                {items.map(item => {
                    switch (item.component) {
                        case 'heading':
                            return (
                                <Heading
                                    key={item._uid}
                                    $style={textComponentStyling[layout]['heading']}
                                    as={item.type || "h2"}
                                    fontKeys={textComponentFontKeys.heading}
                                    {...item.sbEditable}
                                >
                                    {item.text}
                                </Heading>
                            );
                        case 'paragraph':
                            return (
                                <div
                                    key={item._uid}
                                    className={css({
                                        ...responsiveTextStyles(textComponentFontKeys.paragraph),
                                        ...textComponentStyling[layout]['paragraph'],
                                    })}
                                    {...item.sbEditable}
                                >
                                    <RichText data={item.text} />
                                </div>
                            );
                        case 'preamble':
                            return (
                                <div
                                    key={item._uid}
                                    className={css({
                                        ...responsiveTextStyles(textComponentFontKeys.preamble),
                                        ...textComponentStyling[layout]['preamble'],
                                    })}
                                    {...item.sbEditable}
                                >
                                    <RichText data={item.text} />
                                </div>
                            );
                        case 'subHeading':
                            return (
                                <Heading
                                    key={item._uid}
                                    $style={textComponentStyling[layout]['subHeading']}
                                    as={item.type || "h3"}
                                    fontKeys={textComponentFontKeys.subHeading}
                                    {...item.sbEditable}
                                >
                                    {item.text}
                                </Heading>
                            );
                        case 'testimonial':
                            return (
                                <div
                                    key={item._uid}
                                    className={css({
                                        ...responsiveTextStyles(textComponentFontKeys.testimonial),
                                        ...textComponentStyling[layout]['testimonial'],
                                    })}
                                    {...item.text}
                                >
                                    <RichText data={item.text} />
                                </div>
                            );
                        case 'quote':
                            return (
                                <div
                                    key={item._uid}
                                    className={css({ ...textComponentStyling[layout]['quote'] })}
                                    {...item.sbEditable}
                                >
                                    <div className={css({ ...responsiveTextStyles(textComponentFontKeys.quote.text) })}>
                                        <RichText data={item.text} />
                                    </div>
                                    <Paragraph
                                        $style={{ marginTop: '24px' }}
                                        fontKeys={textComponentFontKeys.quote.author}
                                    >
                                        — {item.author}
                                    </Paragraph>
                                </div>
                            );

                        default:
                            return null;
                    }
                })}
            </div>
        </div>
    );
};
Text.propTypes = {
    contentHorizontalAlignment: PropTypes.string,
    id: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.object),
    layout: PropTypes.oneOf(Object.keys(fontKeys)),
};

export default Text;
